import { FC } from 'react';
import { Prompt } from '@/components/MainContent';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts';

interface PromptTabNavigationProps {
  isLoggedIn: boolean;
  activeTab: 'current' | 'playground' | 'versions';
  prompt: Prompt;
  newPrompt?: boolean;
  setActiveTab: (tab: 'current' | 'playground' | 'versions') => void;
  setShowAddToCollectionModal: (show: boolean) => void;
  promptInCollections?: Set<number>;
  isCheckingCollections?: boolean;
}

const PromptTabNavigation: FC<PromptTabNavigationProps> = ({
  isLoggedIn,
  activeTab,
  prompt,
  newPrompt,
  setActiveTab,
  setShowAddToCollectionModal,
  promptInCollections = new Set(),
  isCheckingCollections = false
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isOwner = user?.id === prompt?.user_id;

  // Determine button text based on whether prompt is in collections
  const getButtonText = () => {
    if (isCheckingCollections) {
      return t("checking");
    }
    if (promptInCollections.size > 0) {
      return t("modifyCollectionsAssignments");
    }
    return t("addToCollections");
  };

  const getButtonIcon = () => {
    if (promptInCollections.size > 0) {
      return (
        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    }
    return (
      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    );
  };
  return (
    <div className="border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 transition-colors duration-300">
      <nav className="flex space-x-8 px-6 justify-between">
        <div className='flex space-x-8'>
          {!newPrompt && (
            <button
              onClick={() => setActiveTab('current')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'current'
                ? 'border-brand-electric-blue dark:border-blue-400 text-brand-electric-blue dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-brand-dark-blue dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-600'
                }`}
            >
              {t("overview")}
            </button>
          )}
          {(isOwner || newPrompt) && <button
            onClick={() => setActiveTab('playground')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'playground'
              ? 'border-brand-electric-blue dark:border-blue-400 text-brand-electric-blue dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-brand-dark-blue dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-600'
              }`}
          >
            {t("playground")}
          </button>}
          {!newPrompt && (
            <button
              onClick={() => setActiveTab('versions')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'versions'
                ? 'border-brand-electric-blue dark:border-blue-400 text-brand-electric-blue dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-brand-dark-blue dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-600'
                }`}
            >
              {t("versionHistory")}
            </button>
          )}
        </div>
        {!newPrompt && (
          <div className='flex items-center md:w-1/3 lg:w-[30%] xl:w-1/4 my-2'>
            {isLoggedIn && (
              <button
                onClick={() => setShowAddToCollectionModal(true)}
                className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue dark:bg-blue-600 border border-transparent rounded-md hover:bg-brand-electric-blue dark:hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-brand-electric-blue dark:focus:ring-blue-400 transition-colors duration-200"
              >
                {getButtonIcon()}
                {getButtonText()}
              </button>
            )}
          </div>
        )}
      </nav>
    </div>
  );
};

export default PromptTabNavigation;
